/* eslint-disable max-lines -- Pre-existing violation */
import {isFeatureEnabled} from 'feature-flags';
import {NotificationItemData} from 'platform/components';
import {HStack, Text, ThemeColorTextPath} from 'platform/foundation';
import {useDateTimeFormatter, useLocale} from 'platform/locale';
import {match} from 'ts-pattern';

import {useCallback} from 'react';
import {Trans} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {always, isNil, isNotNil, not} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  Record,
  SentToastNotificationResponseBody,
  taskManagementApi,
  ToastNotificationAutomationRecord,
  useLazyGetMessageByIdQuery,
  useLazyGetSourcingVehicleOfferQuery,
} from '@dms/api/core';
import {useLazyGetUserAvatarUrlQuery} from '@dms/api/customer';
import {FINANCING_ENDING_BULK_ACTION_CODE} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useLazyGetServiceCaseByServiceOrderIdQuery} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import {openEditTaskFormDialog} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {
  accountingRoutes,
  businessCaseRoutes,
  customerRoutes,
  importRoutes,
  interestRoutes,
  orchestrationRoutes,
  rentalOrderRoutes,
  settingsRoutes,
  sourcingRoutes,
  vehiclesRoutes,
  workshopRoutes,
} from '@dms/routes';
import {
  downloadDataGridExport,
  ORDER_TABS,
  queryParams,
  useGetRecordUrlForSignature,
  notificationTypes,
} from '@dms/shared';

import {composePath, EMPTY_PLACEHOLDER, noop, parseDate, useNavigate} from 'shared/web';

import {formatDataGridExportDate} from 'features/eag-grid';

import {addDelegatedForFlag} from '../utils/addDelegatedForFlag';
import {buildPromptBridgeNotification} from '../utils/buildPromptBridgeNotification';
import {composeEntityPath} from '../utils/composeEntityPath';
import {formatDueDateToNaturalLanguage} from '../utils/formatDueDateToNaturalLanguage';
import {getFinancingEndingReminderVehicleSuffix} from '../utils/getFinancingEndingReminderVehicleSuffix';
import {isHiddenRuleNotification} from '../utils/isHiddenRuleNotification';
import {openBulkOperationResultDialog} from '../utils/openBulkOperationResultDialog';

/**
 * A rental vehicle limit is already reached rather than approaching. Not a strict equality check:
 * the availability-to date is reached on the day itself (0 days left), and a single odometer
 * reading can overshoot the mileage limit, leaving a negative distance to it.
 */
const isLimitReached = (remaining: number) => remaining <= 0;

const CUSTOMER_RATING_SEPARATOR = ' · ';

/**
 * The detail page of the record an automation run acted on, addressed by its id alone; none for a
 * task or a complaint. The same pages as `RuleRunSubject` in `@dms/orchestration` opens.
 */
const getAutomationRecordUrl = (record: ToastNotificationAutomationRecord) => {
  const byId = {params: {id: record.id}, isSearchParamsPreserved: false};

  return match<ToastNotificationAutomationRecord['type'], string | null>(record.type)
    .with('vehicle', () => composePath(vehiclesRoutes.detail, byId))
    .with('interest', () => composePath(interestRoutes.detailOverview, byId))
    .with('business_case', () => composePath(businessCaseRoutes.overview, byId))
    .with('invoice', () => composePath(accountingRoutes.invoiceDetail, byId))
    .with('rental', () => composePath(rentalOrderRoutes.vehicleAndCustomer, byId))
    .with('task', 'complaint', () => null)
    .exhaustive();
};

interface UseGetNotificationItemDataProps {
  onNotificationClick?: () => void;
  handleMarkAsRead?: (id: string) => void;
}

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function useGetNotificationItemData(props?: UseGetNotificationItemDataProps) {
  const navigate = useNavigate();
  const [getRecordUrlForSignature] = useGetRecordUrlForSignature();
  const [getServiceCaseByServiceOrderId] = useLazyGetServiceCaseByServiceOrderIdQuery();
  const [getSourcingVehicleOffer] = useLazyGetSourcingVehicleOfferQuery();
  const [getUserAvatarUrl] = useLazyGetUserAvatarUrlQuery();
  const [getMessage] = useLazyGetMessageByIdQuery();
  const formatDateTime = useDateTimeFormatter();
  const dispatch = useDispatch();

  const {language} = useLocale();

  const getURLFromRecord = useCallback(
    (record: Record, commentId?: string) =>
      match(record)
        .with({resourceId: 'VEHICLE'}, () =>
          composePath(vehiclesRoutes.detail, {
            params: {id: record.id},
            queryParams: {[queryParams.COMMENT_ID]: commentId},
            isSearchParamsPreserved: false,
          })
        )
        .with({resourceId: 'CUSTOMER'}, () =>
          composePath(customerRoutes.detail, {
            params: {id: record.id},
            queryParams: {[queryParams.COMMENT_ID]: commentId},
            isSearchParamsPreserved: false,
          })
        )
        .with({resourceId: 'BUSINESS_CASE'}, () =>
          composePath(businessCaseRoutes.overview, {
            params: {id: record.id},
            queryParams: {[queryParams.COMMENT_ID]: commentId},
            isSearchParamsPreserved: false,
          })
        )
        .with({resourceId: 'INTEREST'}, () =>
          composePath(interestRoutes.detailOverview, {
            params: {id: record.id},
            queryParams: {[queryParams.COMMENT_ID]: commentId},
            isSearchParamsPreserved: false,
          })
        )
        .with({resourceId: 'SERVICE_CASE'}, () =>
          composePath(workshopRoutes.serviceCaseDetail, {
            params: {id: record.id},
            queryParams: {[queryParams.COMMENT_ID]: commentId},
            isSearchParamsPreserved: false,
          })
        )
        .with({resourceId: 'SERVICE_ORDER'}, async () => {
          const serviceCaseId = await getServiceCaseByServiceOrderId({serviceOrderId: record.id})
            .unwrap()
            .then((data) => data?.id);

          return isNotNilOrEmpty(serviceCaseId)
            ? composePath(workshopRoutes.serviceCaseDetail, {
                params: {
                  id: serviceCaseId,
                },
                queryParams: {
                  [queryParams.SERVICE_CASE_ORDER_ID]: record.id,
                  [queryParams.SERVICE_CASE_ORDER_TAB]: ORDER_TABS.COMMENTS_NOTES,
                  [queryParams.COMMENT_ID]: commentId,
                },
                isSearchParamsPreserved: false,
              })
            : null;
        })
        .with({resourceId: EntityResourceIds.sourcing}, async () => {
          const offer = await getSourcingVehicleOffer({offerId: record.id})
            .unwrap()
            .catch(always(undefined));
          const externalId = offer?.externalId;
          return isNotNilOrEmpty(externalId)
            ? composePath(sourcingRoutes.vehicleDetail, {
                params: {adId: externalId},
                queryParams: {
                  tab: 'comments',
                  [queryParams.COMMENT_ID]: commentId,
                },
                isSearchParamsPreserved: false,
              })
            : null;
        })
        .otherwise(always(undefined)),
    [getServiceCaseByServiceOrderId, getSourcingVehicleOffer]
  );

  const handleGetUserAvatarUrl = useCallback(
    (userId: string) => async () => {
      const {data: avatarUrl} = await getUserAvatarUrl({userId, height: 40, width: 40});

      return avatarUrl;
    },
    [getUserAvatarUrl]
  );

  const handleGetPromptBridgeMessage = useCallback(
    async (messageId: string) => {
      const message = await getMessage({id: messageId}).unwrap();
      return message;
    },
    [getMessage]
  );

  return useCallback(
    // eslint-disable-next-line max-lines-per-function -- Pre-existing violation
    (notification: SentToastNotificationResponseBody, userId: string) =>
      addDelegatedForFlag(
        match<
          SentToastNotificationResponseBody,
          Promise<NotificationItemData> | NotificationItemData | null
        >(notification)
          .with({type: notificationTypes.PARTICIPATION_ASSIGNEE_ASSIGNED}, async (data) => {
            const recordUrl = await getURLFromRecord(data.payload.record);

            return {
              id: data.id,
              isRead: data.isRead,
              created: parseDate(data.createdAt),
              resource: data.payload.record.name,
              url: recordUrl,
              getAvatarUrl: handleGetUserAvatarUrl(data.payload.actor.id),
              userName: data.payload.actor.name,
              title: (
                <Trans
                  i18nKey="general.notifications.labels.participationAssigneeAssigned"
                  values={{userName: data.payload.actor.name}}
                  components={{bold: <strong />}}
                />
              ),
            };
          })
          .with(
            {
              type: notificationTypes.BULK_OPERATION_STATE_CHANGED,
              payload: {actionCode: FINANCING_ENDING_BULK_ACTION_CODE},
            },
            (data) => {
              if (!isFeatureEnabled(featureFlags.SALES_FINANCING_ENDING)) {
                return null;
              }

              const hasFailedToStart = data.payload.state === 'RESOLVE_FAILED';

              // `doneCount` counts dispatched e-mails, not sent ones, so this item shows no counts.
              return {
                id: data.id,
                isRead: data.isRead,
                icon: 'communication/email',
                created: parseDate(data.createdAt),
                resource: i18n.t('page.businessCases.financingEnding.title'),
                url: composePath(businessCaseRoutes.financingEnding, {
                  queryParams: {
                    [queryParams.BUSINESS_CASE_FINANCING_ENDING_OPERATION]:
                      data.payload.bulkOperationId,
                  },
                  isSearchParamsPreserved: false,
                }),
                title: hasFailedToStart
                  ? i18n.t(
                      'entity.businessCase.financingEnding.notification.sendingFailedToStart.title'
                    )
                  : i18n.t(
                      'entity.businessCase.financingEnding.notification.sendingFinished.title'
                    ),
                message: hasFailedToStart
                  ? i18n.t(
                      'entity.businessCase.financingEnding.notification.sendingFailedToStart.body'
                    )
                  : i18n.t('entity.businessCase.financingEnding.result.finishedNoCounts'),
              };
            }
          )
          .with(
            {type: notificationTypes.BULK_OPERATION_STATE_CHANGED},
            {type: notificationTypes.BULK_OPERATION_RECORDS_CHANGED},
            (data) =>
              isFeatureEnabled(featureFlags.DATA_GRID_BULK_OPERATIONS)
                ? {
                    id: data.id,
                    isRead: data.isRead,
                    created: parseDate(data.createdAt),
                    icon: 'notification/terminal',
                    title: data.payload.actionLabel ?? data.payload.actionCode,
                    resource: i18n.t('general.actions.viewDetail'),
                    message: [
                      `${i18n.t('general.labels.total')}: ${data.payload.totalCount}`,
                      `${i18n.t('general.notifications.success')}: ${data.payload.doneCount}`,
                      `${i18n.t('general.notifications.labels.failed')}: ${data.payload.failedCount}`,
                      `${i18n.t('entity.automationRule.detail.runResult.skipped')}: ${data.payload.skippedCount}`,
                    ].join(' · '),
                    onClick: () => {
                      openBulkOperationResultDialog(
                        data.payload.bulkOperationId,
                        data.payload.actionLabel ?? data.payload.actionCode
                      );
                      props?.onNotificationClick?.();
                    },
                  }
                : null
          )
          .with({type: notificationTypes.DATAGRID_EXPORT}, (data) => {
            const exportedAtDate = data.payload.exportedAt
              ? formatDataGridExportDate(parseDate(data.payload.exportedAt))
              : '';

            return {
              id: data.id,
              isRead: data.isRead,
              icon: 'notification/terminal',
              created: parseDate(data.createdAt),
              resource: i18n.t('general.notifications.labels.datagridExportDownloadFile', {
                resourceName: data.payload.dataGridCode,
                dateTime: exportedAtDate,
                fileFormat: match(data.payload.fileType)
                  .with('CSV', () => 'csv')
                  .with('PDF', () => 'pdf')
                  .otherwise(() => 'xlsx'),
              }),
              onClick: async () => {
                await downloadDataGridExport({
                  exportId: data.payload.exportId,
                  gridCode: data.payload.dataGridCode,
                  exportedAt: exportedAtDate,
                });
                props?.onNotificationClick?.();
              },
              shouldShowPendingState: true,
              title: (
                <Trans
                  i18nKey="general.notifications.labels.datagridExport"
                  values={{
                    exportDate: formatDateTime('dateTimeShort', parseDate(data.payload.exportedAt)),
                  }}
                />
              ),
            };
          })
          .with({type: notificationTypes.PARTICIPATION_ASSIGNEEE_UNASSIGNED}, async (data) => {
            const recordUrl = await getURLFromRecord(data.payload.record);

            return {
              id: data.id,
              isRead: data.isRead,
              created: parseDate(data.createdAt),
              resource: data.payload.record.name,
              url: recordUrl,
              userName: data.payload.actor.name,
              getAvatarUrl: handleGetUserAvatarUrl(data.payload.actor.id),
              title: (
                <Trans
                  i18nKey="general.notifications.labels.participationAssigneeUnassigned"
                  values={{userName: data.payload.actor.name}}
                  components={{bold: <strong />}}
                />
              ),
            };
          })
          .with(
            {
              type: notificationTypes.PARTICIPATION_OWNER_CHANGED,
              // A delegate reads the absent user's copy, so match the user it was meant for.
              payload: {newOwner: {id: notification.delegatedFor?.id ?? userId}},
            },
            {type: notificationTypes.PARTICIPATION_OWNER_ASSIGNED},
            async (data) => {
              const recordUrl = await getURLFromRecord(data.payload.record);

              return {
                id: data.id,
                isRead: data.isRead,
                created: parseDate(data.createdAt),
                resource: data.payload.record.name,
                url: recordUrl,
                userName: data.payload.actor.name,
                getAvatarUrl: handleGetUserAvatarUrl(data.payload.actor.id),
                title: (
                  <Trans
                    i18nKey="general.notifications.labels.participationOwnerAssigned"
                    values={{userName: data.payload.actor.name}}
                    components={{bold: <strong />}}
                  />
                ),
              };
            }
          )
          .with(
            {type: notificationTypes.PARTICIPATION_OWNER_CHANGED},
            {type: notificationTypes.PARTICIPATION_OWNER_UNASSIGNED},
            async (data) => {
              const recordUrl = await getURLFromRecord(data.payload.record);

              return {
                id: data.id,
                isRead: data.isRead,
                created: parseDate(data.createdAt),
                resource: data.payload.record.name,
                url: recordUrl,
                userName: data.payload.actor.name,
                getAvatarUrl: handleGetUserAvatarUrl(data.payload.actor.id),
                title: (
                  <Trans
                    i18nKey="general.notifications.labels.participationOwnerUnassigned"
                    values={{userName: data.payload.actor.name}}
                    components={{bold: <strong />}}
                  />
                ),
              };
            }
          )
          .with({type: notificationTypes.VEHICLE_INSPECTION_EXPIRATION_NOTICE}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.detail, {params: {id: data.payload.vehicle.id}}),
            icon: 'notification/terminal',
            title: (
              <Trans
                count={data.payload.daysUntilExpiration}
                i18nKey="general.notifications.labels.vehicleInspectionExpirationNotice"
                components={{bold: <strong />}}
              />
            ),
          }))
          /*
            Behind the orchestration flag like every other entry into automation rules: the link
            opens a rule page that does not exist while the flag is off.
          */
          .when((data) => isHiddenRuleNotification(data.type), always(null))
          /*
            An automation rule's in-app notification (orchestration 1.0.28; `notified_you` for a
            person named on the rule, 1.0.36). One payload for all four; the type is whom the rule
            notified, and only the wording differs.
          */
          .with(
            {type: notificationTypes.ORCHESTRATION_RULE_TRIGGERED},
            {type: notificationTypes.ORCHESTRATION_RULE_RAN_FOR_RECORD},
            {type: notificationTypes.ORCHESTRATION_RULE_RAN_FOR_TEAM},
            {type: notificationTypes.ORCHESTRATION_RULE_NOTIFIED_YOU},
            (data) => ({
              id: data.id,
              isRead: data.isRead,
              created: parseDate(data.createdAt),
              resource: data.payload.ruleName,
              url:
                (isNotNil(data.payload.record)
                  ? getAutomationRecordUrl(data.payload.record)
                  : null) ??
                composePath(orchestrationRoutes.detail, {params: {ruleId: data.payload.ruleId}}),
              icon: 'notification/terminal',
              title: (
                <Trans
                  /*
                    A rule saved without a name sends ruleName "" — the editor never sets one — and
                    "Rule  ran" names nothing, so it gets copy that does not need a name. The payload is
                    frozen at send time; a name filled in on the backend would be frozen in its
                    language. A run that acted on a record names it whichever way the rule notified.
                  */
                  i18nKey={match({
                    type: data.type,
                    isNamed: isNotNilOrEmpty(data.payload.ruleName),
                    hasRecord: isNotNil(data.payload.record),
                  })
                    .with(
                      {hasRecord: true, isNamed: true},
                      () => 'general.notifications.labels.automationRuleRanForRecordWithRecord'
                    )
                    .with(
                      {hasRecord: true},
                      () =>
                        'general.notifications.labels.automationRuleRanForRecordWithRecordUnnamed'
                    )
                    .with(
                      {type: notificationTypes.ORCHESTRATION_RULE_RAN_FOR_RECORD, isNamed: true},
                      () => 'general.notifications.labels.automationRuleRanForRecord'
                    )
                    .with(
                      {type: notificationTypes.ORCHESTRATION_RULE_RAN_FOR_RECORD},
                      () => 'general.notifications.labels.automationRuleRanForRecordUnnamed'
                    )
                    .with(
                      {type: notificationTypes.ORCHESTRATION_RULE_RAN_FOR_TEAM, isNamed: true},
                      () => 'general.notifications.labels.automationRuleRanForTeam'
                    )
                    .with(
                      {type: notificationTypes.ORCHESTRATION_RULE_RAN_FOR_TEAM},
                      () => 'general.notifications.labels.automationRuleRanForTeamUnnamed'
                    )
                    .with(
                      {type: notificationTypes.ORCHESTRATION_RULE_NOTIFIED_YOU, isNamed: true},
                      () => 'general.notifications.labels.automationRuleNotifiedYou'
                    )
                    .with(
                      {type: notificationTypes.ORCHESTRATION_RULE_NOTIFIED_YOU},
                      () => 'general.notifications.labels.automationRuleNotifiedYouUnnamed'
                    )
                    .with(
                      {isNamed: true},
                      () => 'general.notifications.labels.automationRuleTriggered'
                    )
                    .otherwise(() => 'general.notifications.labels.automationRuleTriggeredUnnamed')}
                  values={{
                    ruleName: data.payload.ruleName,
                    recordLabel: data.payload.record?.label,
                  }}
                  // The rule name is typed by its author and shown to someone else, so it is text:
                  // escaped before the tags are read, and unescaped back into characters after.
                  tOptions={{interpolation: {escapeValue: true}}}
                  shouldUnescape
                  components={{bold: <strong />}}
                />
              ),
            })
          )
          /*
            To the rule owner when a message waits for confirmation or a run stops (T20-142658).
            A run that acted on a record names it, but the link always opens the rule's History
            tab (`HISTORY_TAB` in `@dms/orchestration`): the person sends or skips the message, or
            applies a remediation, there, and the run detail is a dialog without a route. `reason`
            is not shown: it is a code, and its wording comes only from the backend.
          */
          .with(
            {type: notificationTypes.ORCHESTRATION_RUN_AWAITING_CONFIRMATION},
            {type: notificationTypes.ORCHESTRATION_RUN_STOPPED},
            (data) => ({
              id: data.id,
              isRead: data.isRead,
              created: parseDate(data.createdAt),
              resource: data.payload.ruleName,
              url: composePath(orchestrationRoutes.detail, {
                params: {ruleId: data.payload.ruleId},
                queryParams: {[queryParams.COMPONENT_SECTIONS_TAB]: 'history'},
                isSearchParamsPreserved: false,
              }),
              icon: 'notification/terminal',
              title: (
                <Trans
                  i18nKey={match({
                    type: data.type,
                    isNamed: isNotNilOrEmpty(data.payload.ruleName),
                    hasRecord: isNotNil(data.payload.record),
                  })
                    .with(
                      {
                        type: notificationTypes.ORCHESTRATION_RUN_AWAITING_CONFIRMATION,
                        hasRecord: true,
                        isNamed: true,
                      },
                      () =>
                        'general.notifications.labels.automationRunAwaitingConfirmationWithRecord'
                    )
                    .with(
                      {
                        type: notificationTypes.ORCHESTRATION_RUN_AWAITING_CONFIRMATION,
                        hasRecord: true,
                      },
                      () =>
                        'general.notifications.labels.automationRunAwaitingConfirmationWithRecordUnnamed'
                    )
                    .with(
                      {hasRecord: true, isNamed: true},
                      () => 'general.notifications.labels.automationRunStoppedWithRecord'
                    )
                    .with(
                      {hasRecord: true},
                      () => 'general.notifications.labels.automationRunStoppedWithRecordUnnamed'
                    )
                    .with(
                      {
                        type: notificationTypes.ORCHESTRATION_RUN_AWAITING_CONFIRMATION,
                        isNamed: true,
                      },
                      () => 'general.notifications.labels.automationRunAwaitingConfirmation'
                    )
                    .with(
                      {type: notificationTypes.ORCHESTRATION_RUN_AWAITING_CONFIRMATION},
                      () => 'general.notifications.labels.automationRunAwaitingConfirmationUnnamed'
                    )
                    .with(
                      {isNamed: true},
                      () => 'general.notifications.labels.automationRunStopped'
                    )
                    .otherwise(() => 'general.notifications.labels.automationRunStoppedUnnamed')}
                  values={{
                    ruleName: data.payload.ruleName,
                    recordLabel: data.payload.record?.label,
                  }}
                  tOptions={{interpolation: {escapeValue: true}}}
                  shouldUnescape
                  components={{bold: <strong />}}
                />
              ),
            })
          )
          /*
            To the users who may approve the rule (T20-142658). The approval bar is drawn above every
            tab of the rule page, so the link opens the rule without a tab. The requester is the
            avatar, as the actor of other notifications.
          */
          .with({type: notificationTypes.ORCHESTRATION_RULE_APPROVAL_REQUESTED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.ruleName,
            url: composePath(orchestrationRoutes.detail, {params: {ruleId: data.payload.ruleId}}),
            userName: data.payload.requestedBy.name,
            getAvatarUrl: handleGetUserAvatarUrl(data.payload.requestedBy.id),
            title: (
              <Trans
                i18nKey={
                  isNotNilOrEmpty(data.payload.ruleName)
                    ? 'general.notifications.labels.automationRuleApprovalRequested'
                    : 'general.notifications.labels.automationRuleApprovalRequestedUnnamed'
                }
                values={{ruleName: data.payload.ruleName}}
                tOptions={{interpolation: {escapeValue: true}}}
                shouldUnescape
                components={{bold: <strong />}}
              />
            ),
          }))
          .with({type: notificationTypes.DATAGRID_CUSTOM_PRESET_SHARED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            getAvatarUrl: handleGetUserAvatarUrl(data.payload.sharedByUser.id),
            title: (
              <Trans
                i18nKey="general.notifications.labels.datagridCustomPresetShared"
                values={{
                  name: data.payload.sharedByUser.name,
                  grid: data.payload.datagridCode,
                  presetName: data.payload.datagridPresetTitle,
                }}
                components={{bold: <strong />}}
              />
            ),
          }))
          .with({type: notificationTypes.VEHICLE_NEXT_SERVICE_NOTICE}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'action/today',
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.detail, {params: {id: data.payload.vehicle.id}}),
            title: (
              <Trans
                count={data.payload.daysUntilNextService}
                i18nKey="general.notifications.labels.vehicleServiceNotice"
                components={{bold: <strong />}}
              />
            ),
          }))
          .with({type: notificationTypes.RENTAL_VEHICLE_MILEAGE_LIMIT_NOTICE}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'specification/speedometer',
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            // The rental tab is where the mileage limit and the availability-to date live.
            url: composePath(vehiclesRoutes.rental, {params: {id: data.payload.vehicle.id}}),
            title: match(data.payload.kmUntilLimit)
              .when(
                isLimitReached,
                always(i18n.t('general.notifications.labels.rentalVehicleMileageLimitReached'))
              )
              .otherwise((kmUntilLimit) => (
                <Trans
                  count={kmUntilLimit}
                  i18nKey="general.notifications.labels.rentalVehicleMileageLimitNotice"
                  components={{bold: <strong />}}
                />
              )),
          }))
          .with(
            {type: notificationTypes.RENTAL_VEHICLE_AVAILABILITY_EXPIRATION_NOTICE},
            (data) => ({
              id: data.id,
              isRead: data.isRead,
              icon: 'action/today',
              created: parseDate(data.createdAt),
              resource: data.payload.vehicle.name,
              url: composePath(vehiclesRoutes.rental, {params: {id: data.payload.vehicle.id}}),
              title: match(data.payload.daysUntilUnavailable)
                .when(
                  isLimitReached,
                  always(
                    i18n.t('general.notifications.labels.rentalVehicleAvailabilityExpiresToday')
                  )
                )
                .otherwise((daysUntilUnavailable) => (
                  <Trans
                    count={daysUntilUnavailable}
                    i18nKey="general.notifications.labels.rentalVehicleAvailabilityExpirationNotice"
                    components={{bold: <strong />}}
                  />
                )),
            })
          )
          .with({type: notificationTypes.VEHICLE_FILE_OPREATION_FINISHED}, (data) => {
            if (data.payload.operation === 'ROTATE') {
              return {
                id: data.id,
                isRead: data.isRead,
                created: parseDate(data.createdAt),
                resource: data.payload.vehicle.name,
                url: composePath(vehiclesRoutes.photos, {
                  params: {id: data.payload.vehicle.id, tab: 'vehicle-photos'},
                }),
                icon: 'image/photo',
                title: i18n.t('general.notifications.labels.vehiclePhotoRotationFinished'),
              };
            }

            if (data.payload.operation === 'REMOVE_BACKGROUND') {
              return {
                id: data.id,
                isRead: data.isRead,
                created: parseDate(data.createdAt),
                resource: data.payload.vehicle.name,
                url: composePath(vehiclesRoutes.photos, {
                  params: {id: data.payload.vehicle.id, tab: 'vehicle-photos'},
                }),
                icon: 'image/photo',
                title: i18n.t('general.notifications.labels.vehicleBackgroundReplacementFinished'),
              };
            }

            return null;
          })
          .with({type: notificationTypes.VEHICLE_PHOTO_ROTATE_FINISHED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.photos, {
              params: {id: data.payload.vehicle.id, tab: 'vehicle-photos'},
            }),
            icon: 'image/photo',
            title: i18n.t('general.notifications.labels.vehiclePhotoRotationFinished'),
          }))
          .with({type: notificationTypes.CUSTOM_CATALOGUE_REMAP_FINISHED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.modelFamily ?? data.payload.make,
            url: settingsRoutes.vehicleCustomCatalogue,
            icon: 'maps/directions_car',
            // From the payload, not the row the dialog was opened with: the worker reports what it
            // actually moved.
            title: match(data.payload.result)
              .with('succeeded', () =>
                i18n.t('entity.customCatalogue.notifications.successfullyReplaced', {
                  vehicleCount: data.payload.updatedVehicleCount,
                })
              )
              .with('alreadyRemapped', () =>
                i18n.t('entity.customCatalogue.notifications.entryNoLongerExists')
              )
              .exhaustive(),
          }))
          .with({type: notificationTypes.VEHICLE_BACKGROUND_REMOVAL_FINISHED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.photos, {
              params: {id: data.payload.vehicle.id, tab: 'vehicle-photos'},
            }),
            icon: 'image/photo',
            title: i18n.t('general.notifications.labels.vehicleBackgroundReplacementFinished'),
          }))
          .with({type: notificationTypes.SALE_VEHICLE_BACKGROUND_REMOVAL_FINISHED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.saleVehicle.vehicleName,
            url: composePath(vehiclesRoutes.photos, {
              params: {id: data.payload.saleVehicle.vehicleId, tab: 'vehicle-photos'},
            }),
            icon: 'image/photo',
            title: i18n.t('general.notifications.labels.vehicleBackgroundReplacementFinished'),
          }))
          .with({type: notificationTypes.COMMENT_MENTION}, async (data) => {
            const recordUrl = await getURLFromRecord(data.payload.record, data.payload.commentId);

            const commonProps = {
              id: data.id,
              isRead: data.isRead,
              created: parseDate(data.createdAt),
              resource: data.payload.record.name,
              userName: data.payload.author.name,
              getAvatarUrl: handleGetUserAvatarUrl(data.payload.author.id),
              message: data.payload.commentHtml,
              title: (
                <Trans
                  i18nKey="general.notifications.labels.mention"
                  values={{user: data.payload.author.name}}
                  components={{bold: <strong />}}
                />
              ),
            } as const;

            if (data.payload.record.resourceId === 'TASK') {
              return {
                ...commonProps,
                onClick: () => {
                  dispatch(taskManagementApi.util.invalidateTags(['comments']));
                  props?.onNotificationClick?.();
                  openEditTaskFormDialog({
                    id: data.payload.record.id,
                    'data-testid': 'task-notification',
                  });
                },
              };
            }

            return {
              ...commonProps,
              url: recordUrl,
            };
          })
          .with({type: notificationTypes.COMMENT_RECORD_OWNER}, async (data) => {
            const recordUrl = await getURLFromRecord(data.payload.record, data.payload.commentId);

            return {
              id: data.id,
              isRead: data.isRead,
              created: parseDate(data.createdAt),
              resource: data.payload.record.name,
              url: recordUrl,
              getAvatarUrl: handleGetUserAvatarUrl(data.payload.author.id),
              userName: data.payload.author.name,
              message: data.payload.commentHtml,
              title: i18n.t('general.notifications.labels.serviceOrderComment', {
                user: data.payload.author.name,
              }),
            };
          })
          .with({type: notificationTypes.SALE_VEHICLE_RESERVATION_CANCELLED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            icon: 'action/settings',
            url: composePath(vehiclesRoutes.detail, {params: {id: data.payload.vehicle.id}}),
            title: i18n.t('general.notifications.labels.saleVehicleReservationCancelled'),
          }))
          .with({type: notificationTypes.VEHICLE_DAYS_SINCE_LAST_REPRICE_NOTICE}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'notification/terminal',
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.detail, {params: {id: data.payload.vehicle.id}}),
            title: (
              <Trans
                count={data.payload.daysSinceLastReprice}
                i18nKey="general.notifications.labels.vehicleDaysSinceLastReprice"
                components={{bold: <strong />}}
              />
            ),
          }))
          .with({type: notificationTypes.VEHICLE_WARRANTY_EXPIRATION_NOTICE}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'notification/terminal',
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.detail, {params: {id: data.payload.vehicle.id}}),
            title: (
              <Trans
                count={data.payload.daysUntilExpiration}
                i18nKey="general.notifications.labels.warrantyExpiration"
                components={{bold: <strong />}}
              />
            ),
          }))
          .with({type: notificationTypes.CUSTOM_FIELDS_NOTICE}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'notification/terminal',
            created: parseDate(data.createdAt),
            resource: data.payload.record.name,
            url: composeEntityPath(data.payload.record.resourceId, data.payload.record.id),
            title: (
              <Trans
                values={{
                  count: data.payload.daysUntilExpiration,
                  fieldName: data.payload.customFieldName,
                  entityName: data.payload.record.name,
                }}
                i18nKey="general.notifications.labels.customFieldExpiration"
                components={{bold: <strong />}}
              />
            ),
          }))
          .with({type: notificationTypes.DIGITAL_DOCUMENT_SIGNED}, async (data) => {
            const recordUrl = await getRecordUrlForSignature(data.payload.record);

            const transactionTypeTitle =
              data.payload.transactionType === 'remote'
                ? i18n.t('entity.document.labels.remote')
                : i18n.t('entity.document.labels.onSite');

            const documentMessage = `${i18n.t('general.notifications.labels.documentHasBeenSignedBy')} ${data.payload.signedBy.name} (${transactionTypeTitle})`;

            return {
              id: data.id,
              isRead: data.isRead,
              url: recordUrl,
              resource: data.payload.documentName,
              message: documentMessage,
              created: parseDate(data.createdAt),
              userName: data.payload.signedBy.name,
              getAvatarUrl: handleGetUserAvatarUrl(data.payload.signedBy.id),
              title: i18n.t('general.notifications.labels.documentHasBeenSigned'),
            };
          })
          .with({type: notificationTypes.TASK_CREATED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.task.title,
            onClick: () => {
              props?.onNotificationClick?.();
              openEditTaskFormDialog({
                id: data.payload.task.id,
                'data-testid': 'task-notification',
              });
            },
            title: (
              <Trans
                i18nKey="general.notifications.labels.assignedTaskToYou"
                values={{name: data.payload.user.name}}
                components={{bold: <strong />}}
              />
            ),
            userName: data.payload.user.name,
            getAvatarUrl: handleGetUserAvatarUrl(data.payload.user.id),
          }))
          .with({type: notificationTypes.TASK_UPDATED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.task.title,
            onClick: () => {
              props?.onNotificationClick?.();
              openEditTaskFormDialog({
                id: data.payload.task.id,
                'data-testid': 'task-notification',
              });
            },
            title: (
              <Trans
                i18nKey="general.notifications.labels.updatedTask"
                values={{name: data.payload.user.name}}
                components={{bold: <strong />}}
              />
            ),
            userName: data.payload.user.name,
            getAvatarUrl: handleGetUserAvatarUrl(data.payload.user.id),
          }))
          .with({type: notificationTypes.TASK_DUE_DATE_NOTICE}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.task.title,
            onClick: () => {
              props?.onNotificationClick?.();
              openEditTaskFormDialog({
                id: data.payload.task.id,
                'data-testid': 'task-notification',
              });
            },
            title: formatDueDateToNaturalLanguage(parseDate(data.payload.dueDate), language),
          }))
          .with({type: notificationTypes.TASK_REPORTER_CHANGED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.task.title,
            onClick: () => {
              props?.onNotificationClick?.();
              openEditTaskFormDialog({
                id: data.payload.task.id,
                'data-testid': 'task-notification',
              });
            },
            title: (
              <Trans
                i18nKey="general.notifications.labels.taskReporterChanged"
                values={{name: data.payload.user.name}}
                components={{bold: <strong />}}
              />
            ),
            userName: data.payload.user.name,
            getAvatarUrl: handleGetUserAvatarUrl(data.payload.user.id),
          }))
          .with({type: notificationTypes.TASK_COMMENTED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.task.title,
            message: data.payload.commentHtml,
            onClick: () => {
              dispatch(taskManagementApi.util.invalidateTags(['comments']));
              props?.onNotificationClick?.();
              openEditTaskFormDialog({
                id: data.payload.task.id,
                'data-testid': 'task-notification',
              });
            },
            title: (
              <Trans
                i18nKey="general.notifications.labels.serviceOrderComment"
                values={{user: data.payload.user.name}}
                components={{bold: <strong />}}
              />
            ),
            userName: data.payload.user.name,
            getAvatarUrl: handleGetUserAvatarUrl(data.payload.user.id),
          }))
          .with({type: notificationTypes.VEHICLE_UNREGISTERED_NOTICE}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'notification/terminal',
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.detail, {params: {id: data.payload.vehicle.id}}),
            title: (
              <Trans
                count={data.payload.daysUntilExpiration}
                i18nKey="general.notifications.labels.vehicleUnregisteredNotice"
                components={{bold: <strong />}}
              />
            ),
          }))
          .with({type: notificationTypes.ADVERTISEMENT_PUBLISHING_FAILED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.publishing, {
              params: {id: data.payload.vehicle.id},
            }),
            icon: 'alert/error_outline',
            title: i18n.t('general.notifications.labels.advertisementPublishingFailed'),
          }))
          .with({type: notificationTypes.MAGIC_PUBLISHING_FINISHED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.saleVehicle.vehicleName,
            url: composePath(vehiclesRoutes.publishing, {
              params: {id: data.payload.saleVehicle.vehicleId},
            }),
            icon: 'action/check_circle',
            title: i18n.t('general.notifications.labels.magicPublishingFinished'),
          }))
          .with({type: notificationTypes.SALE_VEHICLE_PHOTO_CHECKER_REPORT_FAILED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.saleVehicle.vehicleName,
            url: composePath(vehiclesRoutes.photos, {
              params: {id: data.payload.saleVehicle.vehicleId, tab: 'vehicle-photos'},
            }),
            icon: 'image/photo',
            title: i18n.t('general.notifications.labels.photoCheckerFailed', {
              vehicleName: data.payload.saleVehicle.vehicleName,
            }),
          }))
          .with({type: notificationTypes.SALE_VEHICLE_PHOTO_CHECKER_REPORT_LOW_SCORE}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.saleVehicle.vehicleName,
            url: composePath(vehiclesRoutes.photos, {
              params: {id: data.payload.saleVehicle.vehicleId, tab: 'vehicle-photos'},
            }),
            icon: 'image/photo',
            title: i18n.t('general.notifications.labels.photoCheckerLowScore', {
              vehicleName: data.payload.saleVehicle.vehicleName,
              overallScore: data.payload.overallScore,
            }),
          }))
          .with({type: notificationTypes.SOURCING_FILTER_PRESET_UPDATE}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.filterPresetName,
            url: composePath(sourcingRoutes.classifieds, {
              queryParams: {filterPresetId: data.payload.filterPresetId},
            }),
            icon: 'notification/terminal',
            title: i18n.t('general.notifications.labels.sourcingFilterPresetUpdate', {
              count: data.payload.newVehiclesCount,
            }),
          }))
          .with({type: notificationTypes.VEHICLE_INSPECTION_CLONED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.inspectionDetail, {
              params: {id: data.payload.vehicle.id, inspectionId: data.payload.inspection.id},
            }),
            icon: 'notification/terminal',
            title: i18n.t('general.notifications.labels.vehicleInspectionCloned'),
          }))
          .with({type: notificationTypes.IMPORT_FINISHED}, (data) => {
            const getStatus = () =>
              match(data.payload.importStatus)
                .with('created', () => i18n.t('general.labels.created'))
                .with('scheduled', () => i18n.t('entity.import.labels.scheduled'))
                .with('inProgress', () => i18n.t('general.labels.inProgress'))
                .with('finished', () => i18n.t('entity.import.labels.finished'))
                .with('failed', () => i18n.t('entity.import.labels.failed'))
                .otherwise(() => '');

            const getStatusColor = (): ThemeColorTextPath =>
              match(data.payload.importStatus)
                .with('created', () => 'secondary' as const)
                .with('scheduled', () => 'warning' as const)
                .with('inProgress', () => 'informational' as const)
                .with('finished', () => 'success' as const)
                .with('failed', () => 'danger' as const)
                .otherwise(() => 'secondary' as const);

            return {
              id: data.id,
              isRead: data.isRead,
              created: parseDate(data.createdAt),
              url: composePath(importRoutes.detail, {params: {id: data.payload.importId}}),
              getAvatarUrl: handleGetUserAvatarUrl(data.payload.author.id),
              resource: i18n.t(`entity.import.labels.${data.payload.fileType}`),
              title: (
                <HStack spacing={1}>
                  <Trans
                    i18nKey="general.notifications.labels.importFinished"
                    values={{
                      fileType: i18n.t(`entity.import.labels.${data.payload.fileType}`),
                      importStatus: getStatus(),
                    }}
                  >
                    <Text size="xSmall" color={getStatusColor()} alternative />
                  </Trans>
                </HStack>
              ),
            };
          })
          .with({type: notificationTypes.BUSINESS_CASE_QUICK_UNREAD_MESSAGE}, async (data) => {
            const recordUrl = await getURLFromRecord(data.payload.record);

            return {
              id: data.id,
              isRead: data.isRead,
              created: parseDate(data.createdAt),
              resource: i18n.t('general.notifications.labels.businessCaseQuickUnreadMessageBody', {
                customerFullName: data.payload.customerName,
                businessCaseNumber: data.payload.record.name,
              }),
              url: recordUrl,
              icon: 'communication/chat',
              title: i18n.t('general.notifications.labels.businessCaseQuickUnreadMessage', {
                businessCaseNumber: data.payload.record.name,
              }),
            };
          })
          .with({type: notificationTypes.BUSINESS_CASE_UNREAD_MESSAGE}, async (data) => {
            const recordUrl = await getURLFromRecord(data.payload.record);

            return {
              id: data.id,
              isRead: data.isRead,
              created: parseDate(data.createdAt),
              resource: i18n.t('general.notifications.labels.businessCaseUnreadMessageBody', {
                customerFullName: data.payload.customerName,
                businessCaseNumber: data.payload.record.name,
              }),
              url: recordUrl,
              icon: 'communication/chat',
              title: i18n.t('general.notifications.labels.businessCaseUnreadMessage', {
                businessCaseNumber: data.payload.record.name,
              }),
            };
          })
          .with({type: notificationTypes.CUSTOMER_MESSAGE_NEW_MESSAGE}, async (data) => {
            const record = data.payload.record;

            return {
              id: data.id,
              isRead: data.isRead,
              created: parseDate(data.createdAt),
              resource: isNotNil(record)
                ? i18n.t('general.notifications.labels.customerMessageNewMessageBody', {
                    recordName: record.name,
                  })
                : i18n.t('general.notifications.labels.customerMessageNewMessageBodyNoRecord'),
              // Falls back to the customer, who always exists, when the record has no resolvable URL.
              url:
                (isNotNil(record) ? await getURLFromRecord(record) : null) ??
                composePath(customerRoutes.communication, {
                  params: {id: data.payload.customerId},
                  isSearchParamsPreserved: false,
                }),
              icon: 'communication/email',
              title: i18n.t('general.notifications.labels.customerMessageNewMessage', {
                customerName: data.payload.customerName,
              }),
            };
          })
          .with({type: notificationTypes.CUSTOMER_MESSAGE_UNREAD_MESSAGE}, async (data) => {
            const record = data.payload.record;

            return {
              id: data.id,
              isRead: data.isRead,
              created: parseDate(data.createdAt),
              resource: isNotNil(record)
                ? i18n.t('general.notifications.labels.customerMessageUnreadMessageBody', {
                    recordName: record.name,
                  })
                : i18n.t('general.notifications.labels.customerMessageUnreadMessageBodyNoRecord'),
              url:
                (isNotNil(record) ? await getURLFromRecord(record) : null) ??
                composePath(customerRoutes.communication, {
                  params: {id: data.payload.customerId},
                  isSearchParamsPreserved: false,
                }),
              icon: 'communication/email',
              title: i18n.t('general.notifications.labels.customerMessageUnreadMessage', {
                customerName: data.payload.customerName,
              }),
            };
          })
          .with({type: notificationTypes.VEHICLE_INSPECTION_TO_REVISION}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.inspectionDetail, {
              params: {id: data.payload.vehicle.id, inspectionId: data.payload.inspection.id},
            }),
            icon: 'notification/terminal',
            title: i18n.t('general.notifications.labels.vehicleInspectionToRevision'),
          }))
          .with({type: notificationTypes.VEHICLE_INSPECTION_RETURNED_TO_COMPLETION}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.inspectionDetail, {
              params: {id: data.payload.vehicle.id, inspectionId: data.payload.inspection.id},
            }),
            icon: 'notification/terminal',
            title: i18n.t('general.notifications.labels.vehicleInspectionReturnedToCompletion'),
          }))
          .with({type: notificationTypes.VEHICLE_INSPECTION_FINISHED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.inspectionDetail, {
              params: {id: data.payload.vehicle.id, inspectionId: data.payload.inspection.id},
            }),
            icon: 'notification/terminal',
            title: i18n.t('general.notifications.labels.vehicleInspectionFinished'),
          }))
          .with({type: notificationTypes.PROMPT_BRIDGE_STATUS_CHANGED}, async (data) => {
            const promptBridgeMessage = await handleGetPromptBridgeMessage(data.payload.messageId);

            const promptBridgeNotification = buildPromptBridgeNotification({
              notification: data,
              message: promptBridgeMessage,
            });

            return promptBridgeNotification;
          })
          .with({type: notificationTypes.INTEGRATED_SERVICE_EXTERNAL_PLANNER_CONFLICT}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'alert/error_outline',
            created: parseDate(data.createdAt),
            message: i18n.t('general.notifications.labels.externalPlannerConflictBody', {
              externalEventId: data.payload.externalEventId,
              eventDateTime: formatDateTime('dateTimeShort', parseDate(data.payload.eventDateTime)),
              contactInformation: data.payload.contactInformation,
            }),
            title: i18n.t('general.notifications.labels.externalPlannerConflict'),
          }))
          .with({type: notificationTypes.CAR_PASS_READY}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'notification/terminal',
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.detail, {params: {id: data.payload.vehicle.id}}),
            title: i18n.t('general.notifications.labels.carPassReady'),
          }))
          .with({type: notificationTypes.CAR_PASS_EXPIRED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'action/update',
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.detail, {params: {id: data.payload.vehicle.id}}),
            title: i18n.t('general.notifications.labels.carPassExpired'),
          }))
          .with({type: notificationTypes.CAR_PASS_FAILED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'alert/error_outline',
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.detail, {params: {id: data.payload.vehicle.id}}),
            title: i18n.t('general.notifications.labels.carPassFailed'),
          }))
          .with({type: notificationTypes.CAR_PASS_ACTION_REQUIRED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'alert/warning',
            created: parseDate(data.createdAt),
            resource: data.payload.vehicle.name,
            url: composePath(vehiclesRoutes.detail, {params: {id: data.payload.vehicle.id}}),
            title: i18n.t('general.notifications.labels.carPassActionRequired'),
          }))
          .with({type: notificationTypes.OFFER_VALIDITY_EXPIRATION}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'notification/terminal',
            created: parseDate(data.createdAt),
            resource: data.payload.businessCase.number,
            url: composePath(businessCaseRoutes.overview, {
              params: {id: data.payload.businessCase.id},
            }),
            title: (
              <Trans
                i18nKey="general.notifications.labels.offerValidityExpiration"
                components={{bold: <strong />}}
              />
            ),
          }))
          .with({type: notificationTypes.INTEREST_VEHICLE_MATCH}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            icon: 'action/thumb_up',
            created: parseDate(data.createdAt),
            // Vehicle name is rendered inside the title; empty resource keeps the row clickable.
            resource: '',
            url: composePath(vehiclesRoutes.detail, {params: {id: data.payload.vehicle.id}}),
            title: (
              <Trans
                i18nKey="general.notifications.labels.interestVehicleMatch"
                count={data.payload.interests.length}
                values={{vehicleName: data.payload.vehicle.name}}
                components={{vehicleLink: <Text size="xSmall" color="link" inline />}}
              />
            ),
            actionsPosition: 'aboveCreated',
            actions: data.payload.interests.map((interest) => ({
              title: interest.name,
              variant: 'link',
              size: 'small',
              onClick: (event) => {
                // The whole notification row is clickable and navigates to the
                // vehicle; stop the bubbling so the interest link wins.
                event.stopPropagation();
                if (not(data.isRead)) {
                  props?.handleMarkAsRead?.(data.id);
                }
                navigate(composePath(interestRoutes.detailOverview, {params: {id: interest.id}}));
                props?.onNotificationClick?.();
              },
            })),
          }))
          .with({type: notificationTypes.USER_ABSENCE_DELEGATE_SELECTED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            userName: data.payload.absentUser.name,
            getAvatarUrl: handleGetUserAvatarUrl(data.payload.absentUser.id),
            // An absence has no detail page, so the row is clickable only to mark itself read.
            resource: '',
            onClick: noop,
            title: isNotNil(data.payload.dateTo) ? (
              <Trans
                i18nKey="general.notifications.labels.absenceDelegateSelected"
                values={{
                  userName: data.payload.absentUser.name,
                  dateFrom: formatDateTime('dateTimeMedium', parseDate(data.payload.dateFrom)),
                  dateTo: formatDateTime('dateTimeMedium', parseDate(data.payload.dateTo)),
                }}
                components={{bold: <strong />}}
              />
            ) : (
              <Trans
                i18nKey="general.notifications.labels.absenceDelegateSelectedOpenEnded"
                values={{
                  userName: data.payload.absentUser.name,
                  dateFrom: formatDateTime('dateTimeMedium', parseDate(data.payload.dateFrom)),
                }}
                components={{bold: <strong />}}
              />
            ),
          }))
          .with({type: notificationTypes.USER_ABSENCE_DELEGATION_STARTED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            userName: data.payload.absentUser.name,
            getAvatarUrl: handleGetUserAvatarUrl(data.payload.absentUser.id),
            resource: '',
            onClick: noop,
            title: (
              <Trans
                i18nKey="general.notifications.labels.absenceDelegationStarted"
                values={{userName: data.payload.absentUser.name}}
                components={{bold: <strong />}}
              />
            ),
          }))
          .with({type: notificationTypes.USER_ABSENCE_DELEGATION_ENDED}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            userName: data.payload.absentUser.name,
            getAvatarUrl: handleGetUserAvatarUrl(data.payload.absentUser.id),
            resource: '',
            onClick: noop,
            title: (
              <Trans
                i18nKey="general.notifications.labels.absenceDelegationEnded"
                values={{userName: data.payload.absentUser.name}}
                components={{bold: <strong />}}
              />
            ),
          }))
          .with(
            {type: notificationTypes.BUSINESS_CASE_SELLING_INVOICE_PAID},
            {type: notificationTypes.BUSINESS_CASE_BUYING_INVOICE_PAID},
            (data) => {
              const title = match(data.payload)
                .with({documentType: 'ADVANCE_INVOICE', isFullyPaid: true}, () =>
                  i18n.t('general.notifications.labels.businessCaseAdvanceInvoicePaid', {
                    documentNumber: data.payload.documentNumber,
                  })
                )
                .with({documentType: 'ADVANCE_INVOICE', isFullyPaid: false}, () =>
                  i18n.t('general.notifications.labels.businessCaseAdvanceInvoicePartiallyPaid', {
                    documentNumber: data.payload.documentNumber,
                  })
                )
                .with({documentType: 'FINAL_INVOICE', isFullyPaid: true}, () =>
                  i18n.t('general.notifications.labels.businessCaseFinalInvoicePaid', {
                    documentNumber: data.payload.documentNumber,
                  })
                )
                .with({documentType: 'FINAL_INVOICE', isFullyPaid: false}, () =>
                  i18n.t('general.notifications.labels.businessCaseFinalInvoicePartiallyPaid', {
                    documentNumber: data.payload.documentNumber,
                  })
                )
                .otherwise(always(null));

              if (isNil(title)) {
                return null;
              }

              return {
                id: data.id,
                isRead: data.isRead,
                icon: 'action/payment',
                created: parseDate(data.createdAt),
                resource: [data.payload.businessCase.number, data.payload.vehicle?.name]
                  .filter(isNotNil)
                  .join(' · '),
                url: composePath(businessCaseRoutes.overview, {
                  params: {id: data.payload.businessCase.id},
                  isSearchParamsPreserved: false,
                }),
                title,
              };
            }
          )
          .with({type: notificationTypes.FINANCING_ENDING_REMINDER}, (data) => {
            if (!isFeatureEnabled(featureFlags.SALES_FINANCING_ENDING)) {
              return null;
            }

            const {leadTimeDays, targetEndDates, vehicles} = data.payload;

            return {
              id: data.id,
              isRead: data.isRead,
              icon: 'action/event',
              created: parseDate(data.createdAt),
              resource: i18n.t('page.businessCases.financingEnding.title'),
              // Several end dates cannot be one exact filter, so that link opens the unfiltered list.
              url: composePath(businessCaseRoutes.financingEnding, {
                queryParams:
                  targetEndDates.length === 1
                    ? {[queryParams.BUSINESS_CASE_FINANCING_ENDING_END_DATE]: targetEndDates[0]}
                    : undefined,
                isSearchParamsPreserved: false,
              }),
              title: i18n.t('entity.businessCase.financingEnding.notification.reminder.title', {
                count: Number(leadTimeDays),
                vehicles: vehicles.length,
              }),
              message: vehicles
                .map(
                  (vehicle) =>
                    i18n.t('entity.businessCase.financingEnding.notification.reminder.vehicle', {
                      customerName: vehicle.customerName ?? EMPTY_PLACEHOLDER,
                      vehicleTitle: vehicle.vehicleTitle ?? EMPTY_PLACEHOLDER,
                      endOfFinancing: formatDateTime(
                        'dateShort',
                        parseDate(vehicle.endOfFinancing)
                      ),
                      interpolation: {escapeValue: true},
                    }) + getFinancingEndingReminderVehicleSuffix(vehicle)
                )
                .join('<br />'),
            };
          })
          .with({type: notificationTypes.FINANCING_ENDING_SENDING_FINISHED}, (data) => {
            if (!isFeatureEnabled(featureFlags.SALES_FINANCING_ENDING)) {
              return null;
            }

            return {
              id: data.id,
              isRead: data.isRead,
              icon: 'communication/email',
              created: parseDate(data.createdAt),
              resource: i18n.t('page.businessCases.financingEnding.title'),
              url: composePath(businessCaseRoutes.financingEnding, {
                queryParams: {
                  [queryParams.BUSINESS_CASE_FINANCING_ENDING_OPERATION]:
                    data.payload.bulkOperationId,
                },
                isSearchParamsPreserved: false,
              }),
              title: i18n.t(
                'entity.businessCase.financingEnding.notification.sendingFinished.title'
              ),
              message: i18n.t('entity.businessCase.financingEnding.result.counts', {
                sent: data.payload.sentCount,
                skipped: data.payload.skippedCount,
                failed: data.payload.failedCount,
              }),
            };
          })
          .with({type: notificationTypes.BUSINESS_CASE_CUSTOMER_RATING_RECEIVED}, (data) => {
            const {payload} = data;
            const message = [
              isNotNil(payload.recommendation)
                ? i18n.t('general.notifications.labels.businessCaseCustomerRatingRecommendation', {
                    recommendation: payload.recommendation,
                  })
                : null,
              isNotNilOrEmpty(payload.commentFirstLine)
                ? i18n.t('general.notifications.labels.businessCaseCustomerRatingComment', {
                    comment: payload.commentFirstLine,
                    interpolation: {escapeValue: true},
                  })
                : null,
            ]
              .filter(isNotNil)
              .join(CUSTOMER_RATING_SEPARATOR);

            return {
              id: data.id,
              isRead: data.isRead,
              created: parseDate(data.createdAt),
              title: i18n.t('general.notifications.labels.businessCaseCustomerRatingReceived', {
                customerName: payload.customerName,
                rating: payload.rating,
              }),
              message: isNotNilOrEmpty(message) ? message : undefined,
              resource: [payload.businessCaseNumber, payload.vehicleTitle]
                .filter(isNotNil)
                .join(CUSTOMER_RATING_SEPARATOR),
              url: composePath(businessCaseRoutes.overview, {
                params: {id: payload.businessCaseId},
                isSearchParamsPreserved: false,
              }),
              icon: payload.needsAttention ? 'alert/warning' : 'action/grade',
              flags: payload.needsAttention
                ? [
                    {
                      label: i18n.t('entity.newVehicle.catalogue.needsAttention'),
                      colorScheme: 'orange',
                      isSubtle: true,
                    },
                  ]
                : undefined,
            };
          })
          .with({type: notificationTypes.NEW_VEHICLE_FACTORY_CONFIRMATION}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.orderTitle,
            url: composePath(vehiclesRoutes.newVehicleDetail, {
              params: {id: data.payload.vehicle.id},
            }),
            icon: 'notification/terminal',
            title:
              data.payload.kind === 'delivered'
                ? i18n.t('general.notifications.labels.newVehicleFactoryDelivered', {
                    orderTitle: data.payload.orderTitle,
                  })
                : i18n.t('general.notifications.labels.newVehicleFactoryCancelled', {
                    orderTitle: data.payload.orderTitle,
                  }),
          }))
          .with({type: notificationTypes.NEW_VEHICLE_FACTORY_VIN_CONFLICT}, (data) => ({
            id: data.id,
            isRead: data.isRead,
            created: parseDate(data.createdAt),
            resource: data.payload.orderTitle,
            url: composePath(vehiclesRoutes.newVehicleDetail, {
              params: {id: data.payload.vehicle.id},
            }),
            icon: 'alert/error_outline',
            title: i18n.t('general.notifications.labels.newVehicleFactoryVinConflict', {
              orderTitle: data.payload.orderTitle,
            }),
            message: i18n.t('general.notifications.labels.newVehicleFactoryVinConflictMessage', {
              vin: data.payload.vin,
              interpolation: {escapeValue: true},
            }),
          }))
          .otherwise(always(null)),
        notification.delegatedFor
      ),
    // eslint-disable-next-line react/exhaustive-deps -- formatDateTime rerenders on every render, but its stable
    [
      getRecordUrlForSignature,
      getURLFromRecord,
      handleGetUserAvatarUrl,
      handleGetPromptBridgeMessage,
      navigate,
    ]
  );
}
